import React from 'react'
import { isEmpty } from 'lodash'

import { BasicModal } from '@/components/layout/modals/BasicModal'
// The same LCS this file has always used, moved out so the unified prose diff can run it over WORDS instead of
// lines. Identical algorithm and identical results here — only the item type is now a parameter.
import { longestCommonSubsequence as findCommonLines } from '@/components/diff/diff.helpers'

import './DiffModal.scss'

/**
 * Compare two texts line-by-line and categorize each line as:
 * - 'unchanged': Line exists in both old and new
 * - 'removed': Line only exists in old text
 * - 'added': Line only exists in new text
 */
const computeLineDiff = (oldText, newText) => {
  const oldLines = (oldText || '').split('\n')
  const newLines = (newText || '').split('\n')
  const commonLines = findCommonLines(oldLines, newLines)
  const diffResult = []

  let oldIdx = 0
  let newIdx = 0
  let commonIdx = 0

  while (oldIdx < oldLines.length || newIdx < newLines.length) {
    const currentCommonLine = commonLines[commonIdx]
    const oldLine = oldLines[oldIdx]
    const newLine = newLines[newIdx]

    const oldMatchesCommon = commonIdx < commonLines.length && oldLine === currentCommonLine
    const newMatchesCommon = commonIdx < commonLines.length && newLine === currentCommonLine

    if (oldMatchesCommon && newMatchesCommon) {
      diffResult.push({
        type: 'unchanged',
        oldLine,
        newLine,
        oldLineNum: oldIdx + 1,
        newLineNum: newIdx + 1,
      })
      oldIdx++
      newIdx++
      commonIdx++
    } else if (!oldMatchesCommon && oldIdx < oldLines.length) {
      diffResult.push({
        type: 'removed',
        oldLine,
        newLine: null,
        oldLineNum: oldIdx + 1,
        newLineNum: null,
      })
      oldIdx++
    } else if (newIdx < newLines.length) {
      diffResult.push({
        type: 'added',
        oldLine: null,
        newLine,
        oldLineNum: null,
        newLineNum: newIdx + 1,
      })
      newIdx++
    }
  }

  return diffResult
}

/**
 * Renders a side-by-side diff view (like GitHub's split view).
 */
export const SideBySideDiff = ({ oldText, newText, label }) => {
  const diffLines = computeLineDiff(oldText, newText)
  const hasChanges = diffLines.some((line) => line.type !== 'unchanged')

  if (!hasChanges) {
    return (
      <div className="diff-section">
        <p className="diff-section__label">{label}</p>
        <p className="muted-text">No changes</p>
      </div>
    )
  }

  return (
    <div className="diff-section">
      <p className="diff-section__label">{label}</p>
      <div className="side-by-side-diff">
        <div className="diff-header">
          <div className="diff-header__side diff-header__side--old">Previous</div>
          <div className="diff-header__side diff-header__side--new">Current</div>
        </div>
        <div className="diff-content">
          <div className="diff-pane diff-pane--old">
            {diffLines.map((line) => (
              <div
                key={`old-${line.type}-${line.oldLineNum ?? line.newLineNum}`}
                className={`diff-line ${line.type === 'removed' ? 'diff-line--removed' : ''} ${
                  line.type === 'added' ? 'diff-line--empty' : ''
                }`}
              >
                <span className="diff-line__num">{line.oldLineNum || ''}</span>
                <span className="diff-line__content">{line.oldLine ?? ''}</span>
              </div>
            ))}
          </div>
          <div className="diff-pane diff-pane--new">
            {diffLines.map((line) => (
              <div
                key={`new-${line.type}-${line.newLineNum ?? line.oldLineNum}`}
                className={`diff-line ${line.type === 'added' ? 'diff-line--added' : ''} ${
                  line.type === 'removed' ? 'diff-line--empty' : ''
                }`}
              >
                <span className="diff-line__num">{line.newLineNum || ''}</span>
                <span className="diff-line__content">{line.newLine ?? ''}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Shows a simple "old value → new value" change for settings.
 */
export const ValueChange = ({ oldValue, newValue, label }) => {
  const oldDisplay = String(oldValue ?? '(not set)')
  const newDisplay = String(newValue ?? '(not set)')

  if (oldDisplay === newDisplay) {
    return null
  }

  return (
    <div className="value-diff">
      <span className="value-diff__label">{label}:</span>
      <span className="value-diff__old">{oldDisplay}</span>
      <span className="value-diff__arrow">→</span>
      <span className="value-diff__new">{newDisplay}</span>
    </div>
  )
}

/**
 * Generic modal for showing differences between two objects.
 *
 * @param {boolean} open - Whether modal is visible
 * @param {function} onClose - Close handler
 * @param {object} oldItem - The old/previous version
 * @param {object} newItem - The new/current version
 * @param {string} title - Modal title
 * @param {Array<{key: string, label: string}>} settingsToCompare - Simple value fields to compare
 * @param {Array<{key: string, label: string}>} textFieldsToCompare - Text fields to show as side-by-side diff
 * @param {string} className - Additional CSS class
 */
export const DiffModal = ({
  open,
  onClose,
  oldItem,
  newItem,
  title = 'Changes',
  settingsToCompare = [],
  textFieldsToCompare = [],
  className = '',
}) => {
  if (!oldItem || !newItem) {
    return null
  }

  const changedSettings = settingsToCompare.filter(
    ({ key }) => (oldItem[key] ?? '') !== (newItem[key] ?? '')
  )

  const changedTextFields = textFieldsToCompare.filter(
    ({ key }) => (oldItem[key] || '') !== (newItem[key] || '')
  )

  const hasAnyChanges = !isEmpty(changedSettings) || !isEmpty(changedTextFields)

  return (
    <BasicModal
      show={open}
      onClose={onClose}
      size="large"
      className={`diff-modal ${className}`}
      title={title}
    >
      {!isEmpty(changedSettings) && (
        <div className="diff-settings">
          <p className="diff-section__label">Settings</p>
          <div className="diff-settings__list">
            {changedSettings.map(({ key, label }) => (
              <ValueChange
                key={key}
                label={label}
                oldValue={oldItem[key]}
                newValue={newItem[key]}
              />
            ))}
          </div>
        </div>
      )}

      {changedTextFields.map(({ key, label }) => (
        <SideBySideDiff key={key} oldText={oldItem[key]} newText={newItem[key]} label={label} />
      ))}

      {!hasAnyChanges && <p className="muted-text">No differences found.</p>}
    </BasicModal>
  )
}
